/**
 * ConsentBox Component
 *
 * A reusable modal component for displaying terms of use or other consent agreements.
 * Supports: (1) translationKey with showAllLanguages = true → show JA, EN, VI in one box;
 * (2) translationKey only → show current language; (3) no translationKey → custom JA/EN props.
 */
import React from 'react';
import { useLanguage, type Language } from '../../../../contexts/LanguageContext';
import './consentBox.css';

export interface ConsentBoxProps {
  /** When set, title/description/buttons are read from translations (e.g. 'consentTerms') */
  translationKey?: string;
  /** When true with translationKey, show all three languages (JA, EN, VI) in the same box */
  showAllLanguages?: boolean;
  /** Japanese title (used when translationKey is not set) */
  titleJa?: string;
  /** English title (used when translationKey is not set) */
  titleEn?: string;
  /** Japanese description before link (used when translationKey is not set) */
  descriptionJaBefore?: string;
  /** Japanese link text (used when translationKey is not set) */
  linkTextJa?: string;
  /** Japanese description after link (used when translationKey is not set) */
  descriptionJaAfter?: string;
  /** English description before link (used when translationKey is not set) */
  descriptionEnBefore?: string;
  /** English link text (used when translationKey is not set) */
  linkTextEn?: string;
  /** English description after link (used when translationKey is not set) */
  descriptionEnAfter?: string;
  /** URL for the terms link */
  linkUrl?: string;
  /** Agree button text JA (used when translationKey is not set) */
  agreeTextJa?: string;
  /** Agree button text EN (used when translationKey is not set) */
  agreeTextEn?: string;
  /** Disagree button text JA (used when translationKey is not set) */
  disagreeTextJa?: string;
  /** Disagree button text EN (used when translationKey is not set) */
  disagreeTextEn?: string;
  onAgree: () => void;
  onDisagree: () => void;
}

const LANGUAGES: Language[] = ['ja', 'en', 'vi'];

export default function ConsentBox({
  translationKey,
  showAllLanguages = false,
  titleJa = '利用規約',
  titleEn = 'Terms of use',
  descriptionJaBefore = 'サービスをご利用になる前に、',
  linkTextJa = '利用規約',
  descriptionJaAfter = 'をお読みください。',
  descriptionEnBefore = 'Before using the Service, please read the ',
  linkTextEn = 'Terms and Conditions',
  descriptionEnAfter = '.',
  linkUrl = '#',
  agreeTextJa = '同意する',
  agreeTextEn = 'Agree',
  disagreeTextJa = '同意しない',
  disagreeTextEn = 'Disagree',
  onAgree,
  onDisagree,
}: ConsentBoxProps) {
  const { t, tForLang } = useLanguage();
  const useTranslations = Boolean(translationKey);

  if (useTranslations) {
    const key = translationKey!;
    if (showAllLanguages) {
      return (
        <div className="consent-box__overlay">
          <div className="consent-box consent-box--multi-language">
            {LANGUAGES.map((lang) => (
              <div key={lang} className="consent-box__language-block">
                <h2 className="consent-box__title-ja">{tForLang(lang, `${key}.title`)}</h2>
                <p className="consent-box__text">
                  {tForLang(lang, `${key}.descriptionBefore`)}{' '}
                  <a href={linkUrl} className="consent-box__link" target="_blank" rel="noopener noreferrer">
                    {tForLang(lang, `${key}.linkText`)}
                  </a>
                  {tForLang(lang, `${key}.descriptionAfter`)}
                </p>
              </div>
            ))}
            <div className="consent-box__actions">
              <button type="button" className="consent-box__btn consent-box__btn--agree" onClick={onAgree}>
                {tForLang('ja', `${key}.agree`)} / {tForLang('en', `${key}.agree`)} / {tForLang('vi', `${key}.agree`)}
              </button>
              <button type="button" className="consent-box__btn consent-box__btn--disagree" onClick={onDisagree}>
                {tForLang('ja', `${key}.disagree`)} / {tForLang('en', `${key}.disagree`)} / {tForLang('vi', `${key}.disagree`)}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="consent-box__overlay">
        <div className="consent-box consent-box--single-language">
          <div className="consent-box__title-section">
            <h2 className="consent-box__title-ja">{t(`${key}.title`)}</h2>
          </div>
          <div className="consent-box__description">
            <p className="consent-box__text">
              {t(`${key}.descriptionBefore`)}{' '}
              <a href={linkUrl} className="consent-box__link" target="_blank" rel="noopener noreferrer">
                {t(`${key}.linkText`)}
              </a>
              {t(`${key}.descriptionAfter`)}
            </p>
          </div>
          <div className="consent-box__actions">
            <button type="button" className="consent-box__btn consent-box__btn--agree" onClick={onAgree}>
              {t(`${key}.agree`)}
            </button>
            <button type="button" className="consent-box__btn consent-box__btn--disagree" onClick={onDisagree}>
              {t(`${key}.disagree`)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consent-box__overlay">
      <div className="consent-box">
        <div className="consent-box__title-section">
          <h2 className="consent-box__title-ja">{titleJa}</h2>
          <h3 className="consent-box__title-en">{titleEn}</h3>
        </div>
        <div className="consent-box__description">
          <p className="consent-box__text">
            {descriptionJaBefore}{' '}
            <a href={linkUrl} className="consent-box__link" target="_blank" rel="noopener noreferrer">
              {linkTextJa}
            </a>
            {descriptionJaAfter}
          </p>
          <p className="consent-box__text">
            {descriptionEnBefore}{' '}
            <a href={linkUrl} className="consent-box__link" target="_blank" rel="noopener noreferrer">
              {linkTextEn}
            </a>
            {descriptionEnAfter}
          </p>
        </div>
        <div className="consent-box__actions">
          <button type="button" className="consent-box__btn consent-box__btn--agree" onClick={onAgree}>
            {agreeTextJa} ({agreeTextEn})
          </button>
          <button type="button" className="consent-box__btn consent-box__btn--disagree" onClick={onDisagree}>
            {disagreeTextJa} ({disagreeTextEn})
          </button>
        </div>
      </div>
    </div>
  );
}
